/**
 * WebSocket Hook
 * WebSocket Hook
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { WS_BASE_URL } from "./apiClient";

export const useWebSocket = (organizationId, onMessage) => {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const messageQueueRef = useRef([]);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  useEffect(() => {
    connectWebSocket();

    return () => {
      disconnectWebSocket();
    };
  }, [organizationId]);

  const connectWebSocket = useCallback(() => {
    try {
      if (!organizationId) {
        setIsConnected(false);
        return;
      }

      const token = localStorage.getItem("access_token");
      if (!token) {
        setIsConnected(false);
        return;
      }

      const ws = new WebSocket(
        `${WS_BASE_URL}/ws/chat/org/${organizationId}/?token=${token}`,
      );

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;

        while (messageQueueRef.current.length > 0) {
          const msg = messageQueueRef.current.shift();
          ws.send(JSON.stringify(msg));
        }
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        onMessage(data);
      };

      ws.onerror = (error) => {
        console.error("[WebSocket] Error:", error);
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;

        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(
            3000 * Math.pow(2, reconnectAttemptsRef.current - 1),
            30000,
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error("[WebSocket] Connection error:", error);
      setIsConnected(false);
    }
  }, [organizationId, onMessage]);

  const disconnectWebSocket = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  };

  const sendMessage = useCallback(
    (message) => {
      if (wsRef.current && isConnected) {
        wsRef.current.send(JSON.stringify(message));
      } else {
        // Queue message if not connected
        console.log("[WebSocket] Queuing message (not connected):", message);
        messageQueueRef.current.push(message);
      }
    },
    [isConnected],
  );

  return {
    isConnected,
    sendMessage,
    disconnect: disconnectWebSocket,
  };
};
